import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Phone,
  Plus,
  Trash2,
  Star,
  Check,
  Loader2,
  Shield,
  Mail,
  Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ProfileSettingsPage = () => {
  const navigate = useNavigate();
  const { user: authUser, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [newPhone, setNewPhone] = useState('');
  const [addingPhone, setAddingPhone] = useState(false);
  
  // Fetch user profile
  useEffect(() => {
    if (!token || !authUser) {
      navigate('/login');
      return;
    }
    
    fetchProfile();
  }, [token, authUser, navigate]);
  
  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };
  
  // Add new phone number
  const handleAddPhone = async () => {
    if (!newPhone.trim()) return;
    
    // Basic validation
    const cleaned = newPhone.replace(/\D/g, '');
    if (cleaned.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    try {
      setAddingPhone(true);
      const response = await fetch(`${API}/api/auth/phone-numbers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone: cleaned })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Phone number added');
        setNewPhone('');
        fetchProfile();
        if (refreshUser) refreshUser();
      } else {
        toast.error(data.detail || 'Failed to add phone number');
      }
    } catch (error) {
      toast.error('Failed to add phone number');
    } finally {
      setAddingPhone(false);
    }
  };
  
  // Remove phone number
  const handleRemovePhone = async (phone) => {
    if (!confirm('Remove this phone number?')) return;
    
    try {
      const response = await fetch(`${API}/api/auth/phone-numbers/${phone}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Phone number removed');
        fetchProfile();
        if (refreshUser) refreshUser();
      } else {
        toast.error(data.detail || 'Failed to remove phone number');
      }
    } catch (error) {
      toast.error('Failed to remove phone number');
    }
  };
  
  // Set primary phone
  const handleSetPrimary = async (phone) => {
    try {
      const response = await fetch(`${API}/api/auth/phone-numbers/primary`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone })
      });
      
      if (response.ok) {
        toast.success('Primary phone updated');
        fetchProfile();
        if (refreshUser) refreshUser();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update primary phone');
      }
    } catch (error) {
      toast.error('Failed to update primary phone');
    }
  };
  
  // Format phone for display
  const formatPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    return phone;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }
  
  if (!profile) return null;
  
  const phoneNumbers = profile.phone_numbers || [];
  const primaryPhone = profile.phone ? profile.phone.replace(/\D/g, '').slice(-10) : null;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <User className="w-8 h-8 text-emerald-400" />
          Profile Settings
        </h1>
        <p className="text-slate-400 mt-1">
          Manage your account settings and phone numbers
        </p>
      </div>
      
      <div className="max-w-2xl space-y-6">
        {/* Basic Info */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-400" />
              Account Information
            </CardTitle>
            <CardDescription className="text-slate-400">
              Your account details (read-only)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Name</label>
                <p className="text-white font-medium">{profile.name}</p>
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Role</label>
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                  {profile.role?.toUpperCase()}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Email</label>
              <div className="flex items-center gap-2 text-white">
                <Mail className="w-4 h-4 text-slate-400" />
                {profile.email}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Phone Numbers */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Phone className="w-5 h-5 text-emerald-400" />
              Phone Numbers
            </CardTitle>
            <CardDescription className="text-slate-400">
              Manage your phone numbers for click-to-call. You can add up to 5 numbers.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Existing Numbers */}
            {phoneNumbers.length === 0 && !primaryPhone ? (
              <div className="text-center py-6 text-slate-400">
                <Phone className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No phone numbers added yet</p>
                <p className="text-sm">Add a phone number to use click-to-call</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="phone-numbers-list">
                {phoneNumbers.map((phone, index) => {
                  const isPrimary = phone === primaryPhone;
                  
                  return (
                    <div
                      key={phone}
                      data-testid={`phone-item-${phone}`}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isPrimary 
                          ? 'bg-emerald-500/10 border-emerald-500/30' 
                          : 'bg-slate-900/50 border-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Phone className={`w-4 h-4 ${isPrimary ? 'text-emerald-400' : 'text-slate-400'}`} />
                        <span className="text-white font-mono">{formatPhone(phone)}</span>
                        {isPrimary && (
                          <Badge className="bg-emerald-500/20 text-emerald-400 text-xs" data-testid="primary-badge">
                            Primary
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {!isPrimary && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-emerald-400"
                            onClick={() => handleSetPrimary(phone)}
                            title="Set as primary"
                            data-testid={`set-primary-${phone}`}
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {phoneNumbers.length > 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-red-400"
                            onClick={() => handleRemovePhone(phone)}
                            title="Remove"
                            data-testid={`remove-phone-${phone}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Add New Number */}
            {phoneNumbers.length < 5 && (
              <div className="pt-4 border-t border-slate-700">
                <label className="text-sm text-slate-400 block mb-2">Add New Phone Number</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">+91</span>
                    <Input
                      type="tel"
                      placeholder="Enter 10-digit number"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="pl-12 bg-slate-900 border-slate-600 text-white"
                      maxLength={10}
                      data-testid="phone-number-input"
                    />
                  </div>
                  <Button
                    onClick={handleAddPhone}
                    disabled={addingPhone || newPhone.length < 10}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="add-phone-button"
                  >
                    {addingPhone ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Add
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {phoneNumbers.length}/5 phone numbers added
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Click-to-Call Info */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Phone className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Click-to-Call</h3>
                <p className="text-slate-400 text-sm mt-1">
                  When you initiate a call from the CRM, Exotel will first call your primary phone number.
                  Once you answer, it will connect you to the client. You can select a different number
                  when making the call if you have multiple numbers configured.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfileSettingsPage;
